import React, { useState, useCallback } from 'react';
import { useFetch } from '../hooks/useFetch';
import { userAPI, withdrawalAPI } from '../api/client';
import { Card, Badge, Alert, Btn, FormInput, FormSelect, Spinner, EmptyState, fmt, fmtDate } from '../components/UI';

const METHODS = [
  { value:'bank_transfer', label:'🏦 Bank Transfer (ACH / BACS)' },
  { value:'crypto_usdt',   label:'₿ Crypto — USDT (TRC20)' },
  { value:'paypal',        label:'🅿 PayPal' },
];

export default function Withdraw() {
  const [amount,  setAmount]  = useState('');
  const [method,  setMethod]  = useState('bank_transfer');
  const [details, setDetails] = useState({ account_number:'', bank_name:'', wallet_address:'', paypal_email:'' });
  const [submitting, setSubmitting] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  const fetchDash = useCallback(() => userAPI.dashboard(), []);
  const { data: dashData, loading: dashLoading, refetch: refetchDash } = useFetch(fetchDash);

  const fetchW = useCallback(() => withdrawalAPI.list(), []);
  const { data: wData, loading: wLoading, refetch: refetchW } = useFetch(fetchW);

  const available = dashData?.stats?.available_balance || 0;
  const withdrawals = wData?.withdrawals || [];

  const QUICK = [50, 100, 250, 500];

  async function submit(e) {
    e.preventDefault();
    setError(''); setSuccess('');
    const amt = parseFloat(amount);
    if (!amt || amt < 10)         { setError('Minimum withdrawal is $10.');         return; }
    if (amt > available)          { setError('Amount exceeds available balance.');   return; }
    if (method === 'bank_transfer' && !details.account_number) { setError('Account number required.'); return; }
    if (method === 'crypto_usdt'  && !details.wallet_address)  { setError('Wallet address required.'); return; }
    if (method === 'paypal'       && !details.paypal_email)    { setError('PayPal email required.');    return; }

    setSubmitting(true);
    try {
      const payload = { amount: amt, payment_method: method, payment_details: details };
      await withdrawalAPI.create(payload);
      setSuccess(`Withdrawal of ${fmt(amt)} submitted successfully! Our team will process it within 1–3 business days.`);
      setAmount('');
      refetchDash();
      refetchW();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit withdrawal.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <h1 style={styles.h1}>Withdraw Earnings</h1>
        <p style={styles.sub}>Transfer your available balance to your preferred payment method.</p>
      </div>

      <div style={styles.grid} className="withdraw-layout">
        {/* Form */}
        <div>
          {/* Balance display */}
          <div style={styles.balanceCard}>
            <div style={styles.balLabel}>Available to Withdraw</div>
            {dashLoading ? <div style={{ color:'rgba(255,255,255,.5)', fontSize:18 }}>Loading…</div>
              : <div style={styles.balAmount}>{fmt(available)}</div>}
            <div style={styles.balSub}>Min. withdrawal: $10 · Processing fee: 1.5%</div>
          </div>

          <Card>
            <Alert type="error"   message={error}   />
            <Alert type="success" message={success} />
            <form onSubmit={submit}>
              {/* Amount */}
              <div style={{ marginBottom:16 }}>
                <label style={styles.label}>Withdrawal Amount</label>
                <div style={styles.amountWrap}>
                  <span style={styles.amountPrefix}>$</span>
                  <input
                    type="number" min="10" max={available} step="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    style={styles.amountInput}
                  />
                </div>
                <div style={{ display:'flex', gap:8, marginTop:10 }}>
                  {QUICK.map(q => (
                    <button key={q} type="button"
                      onClick={() => setAmount(q <= available ? String(q) : String(available.toFixed(2)))}
                      style={styles.quickBtn}>
                      ${q}
                    </button>
                  ))}
                  <button type="button" onClick={() => setAmount(available.toFixed(2))} style={{ ...styles.quickBtn, background:'#eef1ff', color:'#4f6ef7', borderColor:'#4f6ef7' }}>
                    Max
                  </button>
                </div>
              </div>

              {/* Method */}
              <FormSelect label="Payment Method" options={METHODS} value={method} onChange={e => setMethod(e.target.value)} />

              {/* Bank */}
              {method === 'bank_transfer' && (
                <>
                  <FormInput label="Account Number" placeholder="Your bank account number"
                    value={details.account_number} onChange={e => setDetails(d => ({ ...d, account_number: e.target.value }))} />
                  <FormInput label="Bank Name" placeholder="e.g. Chase, Barclays, TD Bank"
                    value={details.bank_name} onChange={e => setDetails(d => ({ ...d, bank_name: e.target.value }))} />
                </>
              )}

              {/* Crypto */}
              {method === 'crypto_usdt' && (
                <FormInput label="USDT Wallet Address (TRC20)" placeholder="T..."
                  value={details.wallet_address} onChange={e => setDetails(d => ({ ...d, wallet_address: e.target.value }))} />
              )}

              {/* PayPal */}
              {method === 'paypal' && (
                <FormInput label="PayPal Email" type="email" placeholder="your@paypal.com"
                  value={details.paypal_email} onChange={e => setDetails(d => ({ ...d, paypal_email: e.target.value }))} />
              )}

              <div style={{ background:'#fef3c7', border:'1px solid #fcd34d', borderRadius:10, padding:'12px 14px', marginBottom:20, fontSize:13, color:'#92400e' }}>
                ⚠️ A 1.5% processing fee applies. Please double-check your payment details before confirming.
              </div>

              <Btn loading={submitting} type="submit" style={{ width:'100%', justifyContent:'center', padding:'13px', fontSize:15 }}>
                Withdraw Now →
              </Btn>
            </form>
          </Card>
        </div>

        {/* History */}
        <Card title="Withdrawal History">
          {wLoading ? <Spinner /> : withdrawals.length === 0 ? (
            <EmptyState icon="📭" message="No withdrawals yet." />
          ) : (
            withdrawals.map(w => (
              <div key={w.id} style={styles.wRow}>
                <div style={styles.wIcon}>{w.payment_method === 'crypto_usdt' ? '₿' : w.payment_method === 'paypal' ? '🅿' : '🏦'}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:600, color:'#1a1f5e' }}>{fmt(w.amount)}</div>
                  <div style={{ fontSize:12, color:'#9099b4' }}>{w.payment_method.replace('_',' ')} · {fmtDate(w.created_at)}</div>
                  {w.admin_note && <div style={{ fontSize:12, color:'#d97706', marginTop:2 }}>Note: {w.admin_note}</div>}
                </div>
                <Badge status={w.status} />
              </div>
            ))
          )}
        </Card>
      </div>
    </div>
  );
}

const styles = {
  wrap:        { padding:'32px', fontFamily:"'DM Sans',sans-serif" },
  header:      { marginBottom:24 },
  h1:          { fontFamily:"'Sora',sans-serif", fontSize:22, fontWeight:700, color:'#1a1f5e', margin:0 },
  sub:         { fontSize:14, color:'#9099b4', marginTop:4 },
  grid:        { display:'grid', gridTemplateColumns:'1fr 1fr', gap:24, alignItems:'start' },
  balanceCard: { background:'linear-gradient(135deg,#1a1f5e,#2d3494)', borderRadius:18, padding:'28px', marginBottom:20, color:'#fff' },
  balLabel:    { fontSize:13, opacity:.65, marginBottom:8 },
  balAmount:   { fontFamily:"'Sora',sans-serif", fontSize:40, fontWeight:700, lineHeight:1 },
  balSub:      { fontSize:13, opacity:.55, marginTop:8 },
  label:       { display:'block', fontSize:13, fontWeight:600, color:'#2e3456', marginBottom:8 },
  amountWrap:  { position:'relative' },
  amountPrefix:{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', fontSize:20, fontWeight:700, color:'#c8cedf' },
  amountInput: { width:'100%', padding:'14px 16px 14px 32px', border:'1.5px solid #e2e6f0', borderRadius:10, fontSize:22, fontWeight:700, fontFamily:"'Sora',sans-serif", color:'#1a1f5e', outline:'none', boxSizing:'border-box' },
  quickBtn:    { padding:'7px 14px', border:'1.5px solid #e2e6f0', borderRadius:8, background:'#f8f9fc', fontSize:13, fontWeight:600, cursor:'pointer', color:'#636b89' },
  wRow:        { display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom:'1px solid #f0f2f8' },
  wIcon:       { width:36, height:36, borderRadius:10, background:'#f0f2f8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 },
};
